import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { AnyAction } from 'redux';

import client from './client';
import { refreshSession } from './rest/refresh_token';
import { SELECTED_USER_HEADER } from 'constant/selectedAccount';
import { setTokens } from 'redux/auth/auth-slice';
import { disableEnabledFeature } from 'redux/user/user-slice';
import { isAuthOriginMismatchError, isInvalidAuthTokenError } from 'utils/authErrors';
import { type AuthLogoutReason, WRONG_PORTAL_LOGOUT_REASON } from 'utils/authSession';
import { getLockedFeatureName } from 'utils/features';
import { getPersistedSelectedUserId } from 'utils/selectedAccount';
import storage from 'utils/storageController';

type RetriableConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
    _skipAuthRefresh?: boolean;
    _wasAuthenticated?: boolean;
};

interface InterceptorStore {
    dispatch: (action: AnyAction) => unknown;
    getState: () => {
        auth: {
            access_token?: string;
        };
    };
}

const isLoginRequest = (config?: RetriableConfig): boolean => {
    const path = config?.url?.split('?')[0].replace(/\/+$/, '');
    return path === '/login' || path?.endsWith('/api/login') === true;
};

// Guard against repeated installation when UserProvider re-renders. Without
// this, each render would stack another interceptor pair on the shared client
// and fan one 401 out into parallel refresh requests.
let installed = false;

const setUpInterceptor = (
    store: InterceptorStore,
    onLogout: (reason?: AuthLogoutReason) => void
) => {
    if (installed) return;
    installed = true;

    client.interceptors.request.use((config) => {
        store.dispatch({ type: 'root/snake-bar/increase' });
        const token = store.getState().auth.access_token;
        const retriableConfig = config as RetriableConfig;
        retriableConfig._wasAuthenticated = Boolean(
            token || storage.getRefreshToken() || config.headers?.Authorization
        );

        if (config.headers && token) {
            config.headers.Authorization = `Bearer ${token}`;

            // Acting profile for EXTERNAL_CLIENT self/spouse switch. Read from
            // localStorage so /me and other bootstrap calls send the header
            // before Redux hydrates. Omit when nothing is persisted.
            const selectedUserId = getPersistedSelectedUserId();
            if (selectedUserId) {
                config.headers[SELECTED_USER_HEADER] = String(selectedUserId);
            }
        }

        return config;
    });

    client.interceptors.response.use(
        (response) => {
            store.dispatch({ type: 'root/snake-bar/decrease' });
            return response;
        },
        async (error: AxiosError) => {
            store.dispatch({ type: 'root/snake-bar/reset' });

            const originalRequest = error.config as RetriableConfig | undefined;
            const status = error.response?.status;

            // Company entitlement lock: hide the surface, do not treat as a
            // generic API failure. Call sites should skip bug toasts.
            if (status === 403) {
                const lockedFeature = getLockedFeatureName(error);
                if (lockedFeature) {
                    store.dispatch(disableEnabledFeature(lockedFeature));
                }
            }

            // These codes identify terminal session failures: refreshing cannot
            // repair a token signed with another secret, and refresh itself is
            // origin-restricted. `/api/login` remains local to the login form.
            const requestWasAuthenticated =
                originalRequest?._wasAuthenticated ??
                Boolean(store.getState().auth.access_token || storage.getRefreshToken());
            const isOriginMismatch = isAuthOriginMismatchError(error);
            const isInvalidAuthToken = isInvalidAuthTokenError(error);
            if (isOriginMismatch || isInvalidAuthToken) {
                if (requestWasAuthenticated && !isLoginRequest(originalRequest)) {
                    onLogout(isOriginMismatch ? WRONG_PORTAL_LOGOUT_REASON : 'session-expired');
                }
                return Promise.reject(error);
            }

            // Bail out of the refresh dance when: the response was not a
            // 401, there's no original config to replay, this request was
            // already retried once (prevent infinite loops if the new token
            // also 401s), or the request itself opted out (the refresh
            // request sets _skipAuthRefresh).
            if (
                status !== 401 ||
                !originalRequest ||
                originalRequest._retry ||
                originalRequest._skipAuthRefresh
            ) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (!storage.getRefreshToken()) {
                onLogout();
                return Promise.reject(error);
            }

            try {
                // Shared in-flight promise — N parallel 401s produce
                // exactly one /api/refresh_token HTTP call.
                const tokens = await refreshSession();
                store.dispatch(setTokens(tokens));

                // Keep both axios instances' default headers in sync for any
                // legacy callsite that talks to the bare axios global rather
                // than the `client` instance.
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + tokens.access_token;
                client.defaults.headers.common['Authorization'] = 'Bearer ' + tokens.access_token;

                if (originalRequest.headers) {
                    originalRequest.headers['Authorization'] = 'Bearer ' + tokens.access_token;
                }

                return client(originalRequest);
            } catch (refreshErr: unknown) {
                if (isAuthOriginMismatchError(refreshErr)) {
                    onLogout(WRONG_PORTAL_LOGOUT_REASON);
                } else if (
                    isInvalidAuthTokenError(refreshErr) ||
                    (axios.isAxiosError(refreshErr) && refreshErr.response?.status === 401)
                ) {
                    onLogout();
                }
                return Promise.reject(refreshErr);
            }
        }
    );
};

export default setUpInterceptor;
